import { useTheme } from "@/src/styles/theme-provider";
import { AddCircle, SearchNormal } from "iconsax-react-nativejs";
import React from "react";
import { useTranslation } from "react-i18next";
import { Pressable, TextInput, useWindowDimensions, View } from "react-native";

type SearchInputProps = Readonly<{
  value?: string;
  onChangeText?: (text: string) => void;
  handleSearch?: () => void;
  placeholderKey?: string;
  containerClassName?: string;
  inputClassName?: string;
  /**
   * When provided, a primary "+" action is rendered inside the search field on
   * mobile (matching the feed). On web this is ignored — use a separate button.
   */
  onCreatePress?: () => void;
  /**
   * Where the search visually sits, so its fill stays visible:
   * - "card" (default): sits inside an elevated card → uses `bg-obsidian`
   *   (matches the feed search; reads black in dark mode).
   * - "screen": sits directly on the page background → uses `bg-ash` so it
   *   stays a distinct field on the obsidian screen.
   */
  surface?: "card" | "screen";
}>;

const ScSearchInput = ({
  value,
  onChangeText,
  placeholderKey = "LABEL_SEARCH_SETTINGS",
  containerClassName = "",
  inputClassName = "",
  handleSearch,
  onCreatePress,
  surface = "card",
}: SearchInputProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const mutedColor = themeVars["--color-ash-1"] as string;
  const surfaceBg = surface === "screen" ? "bg-ash" : "bg-obsidian";

  return (
    <View
      className={`flex-row items-center gap-2 border border-ash-3 ${surfaceBg} px-4 py-3 rounded-full ${containerClassName}`}
    >
      <SearchNormal size={18} color={mutedColor} />

      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={t(placeholderKey)}
        className={`flex-1 text-[15px] text-onyx ${inputClassName}`}
        placeholderTextColor={mutedColor}
        autoCorrect={false}
        autoCapitalize="none"
        onSubmitEditing={() => handleSearch?.()}
        style={{ outlineStyle: "none" } as any}
      />

      {isMobile && onCreatePress && (
        <Pressable
          onPress={onCreatePress}
          hitSlop={8}
          className="active:opacity-70 -mr-1 hover:bg-ash-3 hover:rounded-full"
        >
          <AddCircle
            size={24}
            color={themeVars["--color-primary-default"] as string}
          />
        </Pressable>
      )}
    </View>
  );
};

export default ScSearchInput;
